<template>
  <div class="audio-wolof-wrapper">
    <div class="element-header">
      <span class="element-type-badge">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
          <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
          <line x1="12" y1="19" x2="12" y2="23"></line>
          <line x1="8" y1="23" x2="16" y2="23"></line>
        </svg>
        <span>Audio généré</span>
      </span>
      <div class="element-actions">
        <button class="action-btn" @click.stop="$emit('duplicate')" title="Dupliquer">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
        </button>
        <button class="action-btn delete-btn" @click.stop="$emit('remove')" title="Supprimer">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
        </button>
      </div>
    </div>

    <div class="audio-wolof-content">
      <!-- Condition d'envoi -->
      <label class="field-label">Envoyer seulement si</label>
      <div class="condition-row">
        <div class="condition-variable">
          <VariableSelector
            v-model="cond.variable"
            :variables="variables"
            :allow-custom="true"
            placeholder="Variable (vide = toujours)"
            @change="saveCondition"
          />
        </div>
        <select class="condition-operator" v-model="cond.operator" @change="saveCondition">
          <option value="==">=</option>
          <option value="!=">≠</option>
        </select>
        <div class="condition-value-wrap">
          <input
            class="condition-value"
            type="text"
            v-model="cond.value"
            :placeholder="expectedValues.length ? 'Choisir ou écrire une valeur' : 'Valeur (ex. wolof)'"
            autocomplete="off"
            @focus="showValues = true"
            @blur="onValueBlur"
          />
          <div v-if="showValues && filteredValues.length" class="value-options">
            <button
              v-for="v in filteredValues"
              :key="v.value"
              type="button"
              class="value-option"
              :class="{ selected: v.value === cond.value }"
              @mousedown.prevent="pickValue(v.value)"
            >
              <span class="value-option-value">{{ v.value }}</span>
              <span class="value-option-label" v-if="v.label">{{ v.label }}</span>
            </button>
          </div>
        </div>
      </div>
      <p class="field-hint" v-if="cond.variable && !expectedValues.length">Aucune valeur connue pour cette variable dans les blocs : écris-la à la main.</p>
      <p class="field-hint" v-if="!cond.variable">Sans variable, l'audio est envoyé à tout le monde.</p>

      <!-- Langue de sortie -->
      <label class="field-label">Langue de l'audio</label>
      <div class="langue-choice">
        <button
          v-for="l in langues"
          :key="l.value"
          type="button"
          class="langue-btn"
          :class="{ active: langue === l.value }"
          @click="setLangue(l.value)"
        >{{ l.label }}</button>
      </div>

      <!-- Voix -->
      <label class="field-label">Voix</label>
      <div class="voix-row">
        <select class="voix-select" v-model="voix" @change="onVoixChange">
          <option v-for="v in voixDisponibles" :key="v.nom" :value="v.nom">{{ libelleVoix(v) }}</option>
        </select>
        <span class="field-hint voix-hint">Toutes les voix ne rendent pas pareil en wolof : essaie-en plusieurs, les anciens essais restent dans la liste en bas.</span>
      </div>

      <!-- Texte à dire -->
      <label class="field-label">Texte à dire</label>
      <div class="voice-bubble" :class="{ 'voice-bubble-focus': isFocused }" @click="$refs.textInput.focus()">
        <div class="voice-bubble-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
          </svg>
        </div>
        <textarea
          ref="textInput"
          class="voice-input"
          v-model="text"
          rows="1"
          maxlength="900"
          placeholder="Écris ici ce que la voix doit dire…"
          @input="autoGrow"
          @focus="isFocused = true"
          @blur="isFocused = false; onTextBlur()"
        ></textarea>
      </div>
      <div class="voice-bubble-footer">
        <span class="voice-hint">L'audio se fabrique quand tu sors du champ.</span>
        <span class="char-count" :class="{ 'char-count-limit': text.length >= 900 }">{{ text.length }}/900</span>
      </div>
      <label class="checkbox-line" v-if="langue === 'wolof'">
        <input type="checkbox" v-model="dejaWolof" @change="onTextBlur" />
        <span>Mon texte est déjà en wolof (ne pas traduire)</span>
      </label>
      <label class="checkbox-line">
        <input type="checkbox" v-model="attendre" @change="saveCondition" />
        <span>Attendre 5 secondes après l'audio, pour que le message suivant n'arrive pas avant</span>
      </label>

      <!-- État + audio -->
      <div class="audio-state" :class="stateClass">
        <svg v-if="isGenerating" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="spinner">
          <circle cx="12" cy="12" r="10" opacity="0.3"></circle>
          <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"></path>
        </svg>
        <span>{{ stateLabel }}</span>
        <button class="generate-btn" @click="generate(true)" :disabled="isGenerating || !canGenerate">
          {{ element.link ? 'Regénérer' : 'Générer l\'audio' }}
        </button>
      </div>

      <div v-if="element.link && !isStale" class="audio-ready">
        <audio controls class="audio-player" :key="element.link" :src="element.link"></audio>
        <p class="wolof-text" v-if="element.wolof && !element.deja_wolof && langue === 'wolof'">« {{ element.wolof }} »</p>
        <p class="field-hint">Voix {{ element.voix_audio || 'Kore' }}</p>
      </div>

      <!-- Historique des audios fabriqués avant : on peut en reprendre un -->
      <div v-if="historique.length" class="audio-history">
        <button type="button" class="history-toggle" @click="toggleHistory">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
          </svg>
          <span>Anciens audios ({{ historique.length }})</span>
          <svg class="history-chevron" :class="{ open: showHistory }" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </button>
        <p class="field-hint" v-if="showHistory">Chaque audio fabriqué pour cet élément est gardé ici. « Reprendre » remet son texte et son audio à la place de l'audio actuel (qui rejoint la liste).</p>
        <ul v-if="showHistory" class="history-list">
          <li v-for="(h, i) in historique" :key="h.link" class="history-item">
            <div class="history-meta">
              <span class="history-date">{{ formatDate(h.date) }}</span>
              <span class="history-lang">{{ h.langue_audio === 'fr' ? 'Français' : 'Wolof' }} · voix {{ h.voix || 'Kore' }}</span>
            </div>
            <p class="history-text">{{ h.texte_audio }}</p>
            <p class="history-wolof" v-if="h.wolof && !h.deja_wolof && h.langue_audio !== 'fr'">« {{ h.wolof }} »</p>
            <audio controls preload="none" class="audio-player" :src="h.link"></audio>
            <div class="history-actions">
              <button type="button" class="history-btn history-restore" @click="restore(i)" :disabled="isGenerating">Reprendre cet audio</button>
              <button type="button" class="history-btn history-forget" @click="forget(i)" :disabled="isGenerating">Retirer</button>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { axiosPeelo } from '@/utils/axios'
import VariableSelector from '@/components/common/VariableSelector.vue'

// Historiques dépliés, mémorisés hors du composant : le Playground le recrée après
// chaque sauvegarde (la clé de liste change), ce qui refermerait la liste à chaque action.
const historiquesOuverts = new Set()

// Voix proposées par le service, chargées une fois pour tous les éléments.
const VOIX_DEFAUT = 'Kore'
let voixChargees = null
let chargementVoix = null
async function chargerVoix() {
  if (voixChargees) return voixChargees
  if (!chargementVoix) {
    chargementVoix = (async () => {
      try {
        const token = JSON.parse(localStorage.getItem('user') || '{}')?.data?.token
        const r = await axiosPeelo.get('/voix/voix', { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 })
        if (r.data && Array.isArray(r.data.voix) && r.data.voix.length) voixChargees = r.data.voix
      } catch (e) { /* liste de repli ci-dessous */ }
      return voixChargees || [{ nom: VOIX_DEFAUT, genre: 'F', caractere: 'Ferme' }]
    })()
  }
  return chargementVoix
}

export default {
  name: 'AudioWolofElement',
  components: { VariableSelector },
  props: {
    element: { type: Object, required: true },
    index: { type: Number, required: true },
    idBot: { type: String, default: '' },
    variables: { type: Array, default: () => [] },
    blocks: { type: Array, default: () => [] }
  },
  data() {
    const c = (this.element.conditions && this.element.conditions[0]) || {}
    return {
      text: this.element.text || '',
      dejaWolof: !!this.element.deja_wolof,
      langue: this.element.langue || 'wolof',
      langues: [{ value: 'wolof', label: 'Wolof' }, { value: 'fr', label: 'Français' }],
      voix: this.element.voix || VOIX_DEFAUT,
      voixListe: [],
      // Coché par défaut : sans pause, le texte suivant peut doubler l'audio sur WhatsApp
      attendre: this.element.attendre !== false,
      cond: { variable: c.variable || '', operator: c.operator || '==', value: c.value || '' },
      isGenerating: false,
      isFocused: false,
      showValues: false,
      showHistory: historiquesOuverts.has(`${this.idBot}:${this.index}`),
      error: ''
    }
  },
  async mounted() {
    this.autoGrow()
    this.voixListe = await chargerVoix()
  },
  computed: {
    // Valeurs que la variable choisie peut prendre, lues dans les blocs :
    // boutons ({{variable}}=valeur) et insertions de variable.
    expectedValues() {
      const id = this.cond.variable
      if (!id) return []
      const found = new Map()
      const add = (value, label) => {
        const v = String(value == null ? '' : value).trim()
        if (!v || v.includes('{{') || found.has(v)) return
        found.set(v, { value: v, label: label || '' })
      }
      const fromPayload = (payload) => {
        const m = /^\s*\{\{\s*([^}]+?)\s*\}\}\s*=\s*(.*)$/.exec(String(payload || ''))
        return m && m[1] === id ? m[2] : null
      }
      for (const block of this.blocks || []) {
        for (const el of (block && block.flow) || []) {
          if (el.type === 'text_button' || el.type === 'button_link') {
            for (const b of el.buttons || []) {
              const v = fromPayload(b.payload)
              if (v !== null) add(v, b.title)
            }
          } else if (el.type === 'variable_insert' && el.variable && el.variable.id === id) {
            add(el.variable.value, block.blockname)
          }
        }
      }
      return Array.from(found.values())
    },
    filteredValues() {
      const q = (this.cond.value || '').toLowerCase()
      // Valeur déjà choisie dans la liste : on remontre tout pour pouvoir en changer
      if (!q || this.expectedValues.some(v => v.value === this.cond.value)) return this.expectedValues
      return this.expectedValues.filter(v => v.value.toLowerCase().includes(q) || v.label.toLowerCase().includes(q))
    },
    hasVariables() {
      return this.text.includes('{{')
    },
    // La voix enregistrée sur l'élément reste proposée même si elle manque à la liste du service
    voixDisponibles() {
      const liste = this.voixListe.length ? this.voixListe : [{ nom: VOIX_DEFAUT, genre: 'F', caractere: 'Ferme' }]
      return liste.some(v => v.nom === this.voix) ? liste : [{ nom: this.voix, genre: '', caractere: '' }, ...liste]
    },
    // Audios fabriqués avant pour cet élément (le plus récent en premier), enregistrés avec le bloc
    historique() {
      return Array.isArray(this.element.historique) ? this.element.historique.filter(h => h && h.link) : []
    },
    canGenerate() {
      return this.text.trim().length >= 2 && !this.hasVariables
    },
    // L'audio enregistré ne correspond plus au texte affiché
    isStale() {
      return !!this.element.link && (this.element.texte_audio !== this.text.trim() || !!this.element.deja_wolof !== this.dejaWolof || (this.element.langue_audio || 'wolof') !== this.langue || (this.element.voix_audio || VOIX_DEFAUT) !== this.voix)
    },
    stateClass() {
      if (this.error) return 'state-error'
      if (this.isGenerating) return 'state-busy'
      if (this.element.link && !this.isStale) return 'state-ready'
      return 'state-empty'
    },
    stateLabel() {
      if (this.error) return this.error
      if (this.isGenerating) return 'Fabrication de l\'audio…'
      if (this.hasVariables) return 'Texte avec variables : l\'audio sera fabriqué pendant la conversation'
      if (this.element.link && !this.isStale) return 'Audio prêt'
      if (this.element.link && (this.element.voix_audio || VOIX_DEFAUT) !== this.voix) return 'Voix changée : audio à regénérer'
      if (this.element.link) return 'Texte modifié : audio à regénérer'
      return 'Pas encore d\'audio'
    }
  },
  methods: {
    // La zone de texte grandit avec son contenu
    autoGrow() {
      const el = this.$refs.textInput
      if (!el) return
      el.style.height = 'auto'
      el.style.height = Math.max(el.scrollHeight, 72) + 'px'
    },
    conditions() {
      return this.cond.variable
        ? [{ variable: this.cond.variable, operator: this.cond.operator, value: this.cond.value }]
        : []
    },
    emitUpdate(extra) {
      this.$emit('update', {
        ...this.element,
        text: this.text.trim(),
        deja_wolof: this.dejaWolof,
        langue: this.langue,
        voix: this.voix,
        attendre: this.attendre,
        delai: 5,
        conditions: this.conditions(),
        logic_gate: 'AND',
        ...(extra || {})
      })
    },
    saveCondition() {
      this.emitUpdate()
    },
    // Range l'audio actuel en tête de l'historique (sans doublon, 10 entrées au plus)
    archiver(liste) {
      const e = this.element
      if (!e.link || !e.texte_audio) return liste
      const entree = {
        link: e.link,
        texte_audio: e.texte_audio,
        wolof: e.wolof || '',
        langue_audio: e.langue_audio || 'wolof',
        voix: e.voix_audio || VOIX_DEFAUT,
        deja_wolof: !!e.deja_wolof,
        date: e.genere_le || new Date().toISOString()
      }
      return [entree, ...liste.filter(h => h.link !== entree.link)].slice(0, 10)
    },
    // Remet un ancien audio (et son texte) à la place de l'audio actuel
    restore(i) {
      const h = this.historique[i]
      if (!h || this.isGenerating) return
      const reste = this.historique.filter((_, j) => j !== i)
      const historique = this.archiver(reste).filter(x => x.link !== h.link)
      this.text = h.texte_audio
      this.langue = h.langue_audio || 'wolof'
      this.dejaWolof = !!h.deja_wolof
      this.voix = h.voix || VOIX_DEFAUT
      this.error = ''
      this.emitUpdate({
        link: h.link,
        wolof: h.wolof || '',
        texte_audio: h.texte_audio,
        langue_audio: h.langue_audio || 'wolof',
        voix_audio: h.voix || VOIX_DEFAUT,
        genere_le: h.date || null,
        historique
      })
      this.$nextTick(this.autoGrow)
      this.$emit('notification', { message: 'Ancien audio repris', type: 'success' })
    },
    toggleHistory() {
      this.showHistory = !this.showHistory
      const cle = `${this.idBot}:${this.index}`
      if (this.showHistory) historiquesOuverts.add(cle)
      else historiquesOuverts.delete(cle)
    },
    forget(i) {
      this.emitUpdate({ historique: this.historique.filter((_, j) => j !== i) })
    },
    formatDate(d) {
      const t = d ? new Date(d) : null
      if (!t || isNaN(t.getTime())) return 'Date inconnue'
      return t.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    },
    libelleVoix(v) {
      const genre = v.genre === 'F' ? 'voix féminine' : v.genre === 'M' ? 'voix masculine' : ''
      const detail = [genre, v.caractere ? v.caractere.toLowerCase() : ''].filter(Boolean).join(', ')
      return detail ? `${v.nom} (${detail})` : v.nom
    },
    onVoixChange() {
      this.onTextBlur()
    },
    setLangue(value) {
      if (this.langue === value) return
      this.langue = value
      this.onTextBlur()
    },
    pickValue(value) {
      this.cond.value = value
      this.showValues = false
      this.saveCondition()
    },
    onValueBlur() {
      this.showValues = false
      this.saveCondition()
    },
    onTextBlur() {
      const t = this.text.trim()
      if (this.hasVariables) {
        // Pas de pré-génération possible : le moteur synthétise au moment de l'envoi.
        // L'audio déjà fabriqué rejoint l'historique au lieu d'être perdu.
        this.emitUpdate({ link: null, wolof: '', texte_audio: '', historique: this.archiver(this.historique) })
        return
      }
      if (t.length >= 2 && (!this.element.link || this.isStale)) {
        this.generate(false)
      } else {
        this.emitUpdate()
      }
    },
    async generate(force) {
      if (this.isGenerating || !this.canGenerate) return
      const texte = this.text.trim()
      if (!force && this.element.link && !this.isStale) return
      this.isGenerating = true
      this.error = ''
      try {
        const token = JSON.parse(localStorage.getItem('user') || '{}')?.data?.token
        const r = await axiosPeelo.post(
          `/voix/generer/${this.idBot}`,
          { texte, deja_wolof: this.dejaWolof, langue: this.langue, voix: this.voix },
          { headers: { Authorization: `Bearer ${token}` }, timeout: 130000 }
        )
        if (!r.data || !r.data.audio_url) throw new Error('Audio non reçu')
        // L'audio précédent est gardé dans l'historique ; le nouveau n'y figure jamais (même lien si le texte est repris du cache)
        const historique = this.archiver(this.historique).filter(h => h.link !== r.data.audio_url)
        this.emitUpdate({
          link: r.data.audio_url,
          wolof: r.data.wolof || '',
          texte_audio: texte,
          langue_audio: this.langue,
          voix_audio: this.voix,
          genere_le: new Date().toISOString(),
          historique
        })
        this.$emit('notification', { message: 'Audio prêt', type: 'success' })
      } catch (e) {
        this.error = e.response?.data?.message || 'Impossible de fabriquer l\'audio'
        // On garde quand même le texte saisi
        this.emitUpdate()
        this.$emit('notification', { message: this.error, type: 'error' })
      } finally {
        this.isGenerating = false
      }
    }
  }
}
</script>

<style scoped>
.audio-wolof-wrapper {
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.95), rgba(249, 250, 251, 0.9));
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: all 0.2s ease;
  margin-bottom: 20px;
}

.element-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(145deg, rgba(249, 250, 251, 0.8), rgba(243, 244, 246, 0.6));
  border-bottom: 1px solid rgba(229, 231, 235, 0.5);
  border-radius: 12px 12px 0 0;
}

.element-type-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #15803d;
  font-size: 0.875rem;
}

.element-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(249, 250, 251, 0.8));
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.action-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.action-btn.delete-btn:hover {
  background: linear-gradient(145deg, rgba(239, 68, 68, 0.15), rgba(239, 68, 68, 0.05));
  color: #ef4444;
}

.audio-wolof-content {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
  margin-top: 4px;
}

.field-hint {
  margin: 0;
  font-size: 0.75rem;
  color: #6b7280;
}

.condition-row {
  display: flex;
  gap: 8px;
  align-items: stretch;
}

.condition-variable {
  flex: 1 1 45%;
  min-width: 0;
}

.condition-operator {
  flex: 0 0 56px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  text-align: center;
  font-size: 0.9rem;
  color: #111827;
}

.condition-value-wrap {
  position: relative;
  flex: 1 1 35%;
  min-width: 0;
  display: flex;
}

.value-options {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 30;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);
  padding: 4px;
  max-height: 200px;
  overflow-y: auto;
}

.value-option {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border: none;
  background: transparent;
  border-radius: 6px;
  padding: 8px 10px;
  cursor: pointer;
  text-align: left;
}

.value-option:hover,
.value-option.selected {
  background: #f0fdf4;
}

.value-option-value {
  font-size: 0.85rem;
  font-weight: 600;
  color: #111827;
}

.value-option-label {
  font-size: 0.75rem;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.condition-value {
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 0.875rem;
  color: #111827;
  background: #fff;
}

.voice-bubble {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 16px;
  padding: 14px 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
  cursor: text;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.voice-bubble-focus {
  background: #fff;
  border-color: #16a34a;
  box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.12);
}

.voice-bubble-icon {
  flex: 0 0 36px;
  height: 36px;
  border-radius: 50%;
  background: #16a34a;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.voice-input {
  flex: 1;
  min-width: 0;
  min-height: 72px;
  border: none;
  outline: none;
  background: transparent;
  resize: none;
  overflow: hidden;
  padding: 6px 0 0;
  font-size: 15px;
  line-height: 1.6;
  font-family: 'Inter', system-ui, sans-serif;
  color: #1a202c;
  white-space: pre-wrap;
  word-break: break-word;
}

.voice-input::placeholder {
  color: #86a894;
}

.voice-bubble-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 0 4px;
}

.voice-hint,
.char-count {
  font-size: 0.75rem;
  color: #6b7280;
}

.char-count-limit {
  color: #dc2626;
  font-weight: 600;
}

.condition-value:focus {
  outline: none;
  border-color: #16a34a;
  box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.12);
}

.langue-choice {
  display: inline-flex;
  align-self: flex-start;
  background: #f3f4f6;
  border-radius: 10px;
  padding: 3px;
  gap: 2px;
}

.langue-btn {
  border: none;
  background: transparent;
  border-radius: 8px;
  padding: 6px 16px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s ease;
}

.langue-btn.active {
  background: #fff;
  color: #15803d;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

.voix-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.voix-select {
  align-self: flex-start;
  min-width: 260px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 0.875rem;
  color: #111827;
  background: #fff;
}

.voix-select:focus {
  outline: none;
  border-color: #16a34a;
  box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.12);
}

.checkbox-line {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  color: #4b5563;
  cursor: pointer;
}

.audio-state {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 500;
  margin-top: 4px;
}

.audio-state .generate-btn {
  margin-left: auto;
}

.state-empty { background: #f3f4f6; color: #4b5563; }
.state-busy { background: #eff6ff; color: #1d4ed8; }
.state-ready { background: #f0fdf4; color: #15803d; }
.state-error { background: #fef2f2; color: #b91c1c; }

.generate-btn {
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  background: #15803d;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.generate-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.audio-player {
  width: 100%;
}

.wolof-text {
  margin: 6px 0 0;
  font-size: 0.8rem;
  font-style: italic;
  color: #4b5563;
}

.audio-history {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
  padding-top: 8px;
  border-top: 1px dashed #e5e7eb;
}

.history-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  border: none;
  background: transparent;
  padding: 4px 0;
  font-size: 0.8rem;
  font-weight: 600;
  color: #4b5563;
  cursor: pointer;
}

.history-toggle:hover {
  color: #15803d;
}

.history-chevron {
  transition: transform 0.15s ease;
}

.history-chevron.open {
  transform: rotate(180deg);
}

.history-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.history-item {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.history-meta {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 0.72rem;
  color: #6b7280;
}

.history-lang {
  font-weight: 600;
  color: #15803d;
}

.history-text {
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.5;
  color: #1f2937;
  white-space: pre-wrap;
  word-break: break-word;
}

.history-wolof {
  margin: 0;
  font-size: 0.78rem;
  font-style: italic;
  color: #4b5563;
}

.history-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.history-btn {
  border: none;
  border-radius: 6px;
  padding: 5px 10px;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
}

.history-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.history-restore {
  background: #dcfce7;
  color: #15803d;
}

.history-restore:hover:not(:disabled) {
  background: #bbf7d0;
}

.history-forget {
  background: #f3f4f6;
  color: #6b7280;
}

.history-forget:hover:not(:disabled) {
  background: #fee2e2;
  color: #b91c1c;
}

.spinner {
  animation: spin 1s linear infinite;
  flex-shrink: 0;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
